import React, { useState, useEffect } from 'react';
import contentFilterService, { ContentFilterRule, CreateRuleData, ContentCheckResult } from '../../services/contentFilterService';
import {
  ShieldCheckIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BeakerIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

const AdminContentFiltersPage: React.FC = () => {
  const [rules, setRules] = useState<ContentFilterRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [editingRule, setEditingRule] = useState<ContentFilterRule | null>(null);
  const [testContent, setTestContent] = useState('');
  const [testType, setTestType] = useState<'POST' | 'COMMENT' | 'MESSAGE'>('POST');
  const [testResult, setTestResult] = useState<ContentCheckResult | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  
  const [formData, setFormData] = useState<CreateRuleData>({
    name: '',
    type: 'SPAM',
    enabled: true,
    config: {},
    action: 'FLAG',
    scope: 'ALL',
    priority: 50,
  });

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    try {
      setLoading(true);
      const data = await contentFilterService.getAll();
      setRules(data.sort((a, b) => b.priority - a.priority));
    } catch (error) {
      console.error('Failed to load rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingRule) {
        await contentFilterService.update(editingRule.id, formData);
      } else {
        await contentFilterService.create(formData);
      }
      setShowModal(false);
      resetForm();
      loadRules();
    } catch (error) {
      console.error('Failed to save rule:', error);
      alert('Failed to save rule. Please try again.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this filter rule?')) return;
    try {
      await contentFilterService.delete(id);
      loadRules();
    } catch (error) {
      console.error('Failed to delete rule:', error);
      alert('Failed to delete rule. Please try again.');
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await contentFilterService.toggle(id);
      loadRules();
    } catch (error) {
      console.error('Failed to toggle rule:', error);
      alert('Failed to toggle rule. Please try again.');
    }
  };

  const handleTest = async () => {
    if (!testContent.trim()) {
      alert('Please enter some content to test');
      return;
    }
    
    try {
      setTestLoading(true);
      const result = await contentFilterService.checkContent(testContent, testType);
      setTestResult(result);
    } catch (error) {
      console.error('Failed to test content:', error);
      alert('Failed to test content. Please try again.');
    } finally {
      setTestLoading(false);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setEditingRule(null);
    setShowModal(true);
  };

  const openEditModal = (rule: ContentFilterRule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      type: rule.type,
      enabled: rule.enabled,
      config: rule.config,
      action: rule.action,
      scope: rule.scope,
      priority: rule.priority,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'SPAM',
      enabled: true,
      config: {},
      action: 'FLAG',
      scope: 'ALL',
      priority: 50,
    });
  };

  const updateConfig = (key: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      config: { ...prev.config, [key]: value }
    }));
  };

  const getTypeIcon = (type: string) => {
    const colors: Record<string, string> = {
      SPAM: 'bg-red-100 text-red-800',
      PROFANITY: 'bg-orange-100 text-orange-800',
      LINK_LIMIT: 'bg-blue-100 text-blue-800',
      CAPS_LOCK: 'bg-purple-100 text-purple-800',
      DUPLICATE: 'bg-green-100 text-green-800',
      SIMILARITY: 'bg-yellow-100 text-yellow-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getActionBadge = (action: string) => {
    const badges: Record<string, { color: string; text: string }> = {
      FLAG: { color: 'bg-yellow-100 text-yellow-800', text: '🚩 Flag' },
      AUTO_HIDE: { color: 'bg-orange-100 text-orange-800', text: '👁️ Hide' },
      AUTO_DELETE: { color: 'bg-red-100 text-red-800', text: '🗑️ Delete' },
      REQUIRE_REVIEW: { color: 'bg-blue-100 text-blue-800', text: '👀 Review' },
    };
    const badge = badges[action] || badges.FLAG;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ShieldCheckIcon className="h-10 w-10 text-blue-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Content Filter Manager</h1>
                <p className="text-gray-600 mt-1">Configure automated content moderation rules</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowTestModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <BeakerIcon className="h-4 w-4 mr-2" />
                Test Content
              </button>
              <button
                onClick={loadRules}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
              <button
                onClick={openCreateModal}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Rule
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <ShieldCheckIcon className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Rules</p>
                <p className="text-2xl font-bold text-gray-900">{rules.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Rules</p>
                <p className="text-2xl font-bold text-green-600">{rules.filter(r => r.enabled).length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <XCircleIcon className="h-8 w-8 text-gray-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Disabled Rules</p>
                <p className="text-2xl font-bold text-gray-600">{rules.filter(r => !r.enabled).length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-orange-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">High Priority</p>
                <p className="text-2xl font-bold text-orange-600">{rules.filter(r => r.priority >= 75).length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Rules List */}
        <div className="bg-white rounded-lg shadow">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
              <span className="ml-2 text-gray-600">Loading rules...</span>
            </div>
          ) : rules.length === 0 ? (
            <div className="text-center py-12">
              <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No filter rules</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating a new filter rule</p>
              <button
                onClick={openCreateModal}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create First Rule
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rule
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Scope
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rules.map((rule) => (
                    <tr key={rule.id} className={!rule.enabled ? 'opacity-50' : ''}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{rule.name}</div>
                        <div className="text-xs text-gray-500">
                          Created {new Date(rule.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeIcon(rule.type)}`}>
                          {rule.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {rule.scope}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getActionBadge(rule.action)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className={`h-2 rounded-full ${
                                rule.priority >= 75 ? 'bg-red-500' :
                                rule.priority >= 50 ? 'bg-yellow-500' :
                                'bg-green-500'
                              }`}
                              style={{ width: `${rule.priority}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600">{rule.priority}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleToggle(rule.id)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            rule.enabled ? 'bg-blue-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              rule.enabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditModal(rule)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(rule.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} />
              
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                <form onSubmit={handleSubmit}>
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      {editingRule ? 'Edit Filter Rule' : 'Create Filter Rule'}
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="e.g., Spam Link Detection"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Filter Type</label>
                          <select
                            value={formData.type}
                            onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="SPAM">Spam Detection</option>
                            <option value="PROFANITY">Profanity Filter</option>
                            <option value="LINK_LIMIT">Link Limit</option>
                            <option value="CAPS_LOCK">Caps Lock</option>
                            <option value="DUPLICATE">Duplicate Content</option>
                            <option value="SIMILARITY">Content Similarity</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Scope</label>
                          <select
                            value={formData.scope}
                            onChange={(e) => setFormData(prev => ({ ...prev, scope: e.target.value as any }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="ALL">All Content</option>
                            <option value="POST">Posts Only</option>
                            <option value="COMMENT">Comments Only</option>
                            <option value="MESSAGE">Messages Only</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                          <select
                            value={formData.action}
                            onChange={(e) => setFormData(prev => ({ ...prev, action: e.target.value as any }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="FLAG">Flag for Review</option>
                            <option value="AUTO_HIDE">Auto-Hide Content</option>
                            <option value="AUTO_DELETE">Auto-Delete Content</option>
                            <option value="REQUIRE_REVIEW">Require Review</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Priority (1-100)</label>
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={formData.priority}
                            onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      {/* Type-specific configuration */}
                      {formData.type === 'SPAM' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Spam Threshold (0-1)</label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="1"
                            value={formData.config.threshold || 0.7}
                            onChange={(e) => updateConfig('threshold', parseFloat(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      )}

                      {formData.type === 'LINK_LIMIT' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Maximum Links</label>
                          <input
                            type="number"
                            min="1"
                            value={formData.config.maxLinks || 3}
                            onChange={(e) => updateConfig('maxLinks', parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      )}

                      {formData.type === 'CAPS_LOCK' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Max Caps Percentage</label>
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={formData.config.maxCapsPercent || 50}
                            onChange={(e) => updateConfig('maxCapsPercent', parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      )}

                      {formData.type === 'SIMILARITY' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Similarity Threshold (0-1)</label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="1"
                            value={formData.config.similarityThreshold || 0.85}
                            onChange={(e) => updateConfig('similarityThreshold', parseFloat(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      )}

                      <div>
                        <label className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={formData.enabled}
                            onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                          />
                          <span className="font-medium">Enable this rule immediately</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                    <button
                      type="submit"
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      {editingRule ? 'Update Rule' : 'Create Rule'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Test Content Modal */}
        {showTestModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowTestModal(false)} />
              
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Test Content Against Filters
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Content Type</label>
                      <select
                        value={testType}
                        onChange={(e) => setTestType(e.target.value as any)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="POST">Post</option>
                        <option value="COMMENT">Comment</option>
                        <option value="MESSAGE">Message</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Content to Test</label>
                      <textarea
                        value={testContent}
                        onChange={(e) => setTestContent(e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter content to test against active filters..."
                      />
                    </div>

                    {testResult && (
                      <div className={`p-4 rounded-md ${testResult.passed ? 'bg-green-50' : 'bg-red-50'}`}>
                        <div className="flex items-center mb-2">
                          {testResult.passed ? (
                            <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                          ) : (
                            <XCircleIcon className="h-6 w-6 text-red-600 mr-2" />
                          )}
                          <h4 className={`font-medium ${testResult.passed ? 'text-green-900' : 'text-red-900'}`}>
                            {testResult.passed ? 'Content Passed' : 'Content Violations Detected'}
                          </h4>
                        </div>
                        
                        {!testResult.passed && testResult.violations.length > 0 && (
                          <div className="mt-2 space-y-2">
                            {testResult.violations.map((violation, index) => (
                              <div key={index} className="text-sm bg-white p-2 rounded border border-red-200">
                                <p className="font-medium text-red-900">{violation.ruleName}</p>
                                <p className="text-red-700">{violation.message}</p>
                                <p className="text-xs text-red-600 mt-1">
                                  Action: {violation.action}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="mt-3 text-sm">
                          <span className="font-medium">Recommended Action:</span>{' '}
                          <span className={`font-bold ${
                            testResult.recommendedAction === 'ALLOW' ? 'text-green-700' :
                            testResult.recommendedAction === 'FLAG' ? 'text-yellow-700' :
                            'text-red-700'
                          }`}>
                            {testResult.recommendedAction}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                  <button
                    onClick={handleTest}
                    disabled={testLoading || !testContent.trim()}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {testLoading ? 'Testing...' : 'Test Content'}
                  </button>
                  <button
                    onClick={() => {
                      setShowTestModal(false);
                      setTestContent('');
                      setTestResult(null);
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContentFiltersPage;
